import { JOURNAL_BALANCE_TOLERANCE, Money } from '@palaccount/money';

/**
 * FIN-02 §5b/§5c as pure functions: the editor's working lines and the
 * balance test the totals strip draws live. No React — the unit tests
 * exercise these directly, and the editor renders what they return.
 */

/** One line as the editor holds it: strings straight from the inputs. */
export interface EditorLine {
  accountId: string | null;
  memo: string;
  debit: string;
  credit: string;
  /** BR-FIN-02-01's per-currency axis; empty means the base currency. */
  currency: string;
}

export const BLANK_LINE: EditorLine = {
  accountId: null,
  memo: '',
  debit: '',
  credit: '',
  currency: '',
};

/**
 * §5b L3/L4 — "typing here clears the credit on the same line" (and the
 * reverse): a line can never hold both (BR-FIN-02-03, ERR-FIN-02-04).
 */
export function typeAmount(line: EditorLine, side: 'debit' | 'credit', value: string): EditorLine {
  if (side === 'debit') {
    return { ...line, debit: value, credit: value.trim() === '' ? line.credit : '' };
  }
  return { ...line, credit: value, debit: value.trim() === '' ? line.debit : '' };
}

/**
 * BR-FIN-02-11 — deleting a line; if it was the last one, a fresh blank line
 * is created, so the table is never empty.
 */
export function deleteLine(lines: readonly EditorLine[], index: number): EditorLine[] {
  const next = lines.filter((_, i) => i !== index);
  return next.length === 0 ? [{ ...BLANK_LINE }] : next;
}

/**
 * The ARITHMETIC TAG every `Money` in this module carries. Amounts are only
 * ever compared inside their own currency bucket, so one tag keeps the
 * decimal arithmetic honest without pretending a euro is a dollar, and it
 * REACHES NO COLUMN — the entry is saved with each line's own code.
 *
 * It is NOT the company's base currency, which is a fact only the session
 * knows: `computeBalance` takes that as an argument. It was called `BASE`
 * and used for both, which meant a euro company's blank lines were bucketed
 * under 'USD' while the server buckets them under EUR (`entries.ts`: a line
 * that names no currency takes the base), so the strip's printed totals read
 * an empty bucket the moment any line named EUR explicitly.
 */
const TAG = 'USD';

/** A permissive parse of what the input holds; garbage counts as zero. */
function parseAmount(raw: string): Money | null {
  const trimmed = raw.trim().replace(/,/g, '');
  if (trimmed === '') return null;
  if (!/^\d{1,13}(\.\d{1,2})?$/.test(trimmed)) return null;
  return Money.of(trimmed, TAG);
}

export interface BalanceTest {
  /** The BASE-currency totals — what the strip prints. */
  totalDebit: string;
  totalCredit: string;
  /** The largest |debits − credits| of any one currency (§5c). */
  difference: string;
  /** §5c: every currency within half a cent AND total debits above zero. */
  balanced: boolean;
}

/**
 * §5c — the balance test, exactly, and BR-FIN-02-01's qualifier: "the
 * balance rule holds PER CURRENCY". A euro leg cannot offset a dollar one,
 * so each currency is tested on its own and the strip reports the widest
 * gap. An entry whose every line is empty has a difference of zero but is
 * NOT balanced, because its debits are not above zero — an empty journal
 * can never be posted (BR-FIN-02-02).
 *
 * `baseCode` is THE COMPANY's base currency (GEN-107), passed in because only
 * the session knows it. It does two things and both are the server's rules:
 * a line that names no currency is IN the base (`entries.ts`'s documented
 * default), and the strip's printed totals are the base bucket's. Passing
 * 'USD' for a euro company put the blanks in the wrong bucket and then
 * printed a bucket the entry might not even have.
 */
export function computeBalance(lines: readonly EditorLine[], baseCode: string): BalanceTest {
  const buckets = new Map<string, { debits: Money; credits: Money }>();
  for (const line of lines) {
    const currency = line.currency.trim() === '' ? baseCode : line.currency.trim();
    const bucket = buckets.get(currency) ?? { debits: Money.zero(TAG), credits: Money.zero(TAG) };
    const debit = parseAmount(line.debit);
    const credit = parseAmount(line.credit);
    if (debit) bucket.debits = bucket.debits.plus(debit);
    if (credit) bucket.credits = bucket.credits.plus(credit);
    buckets.set(currency, bucket);
  }

  const base = buckets.get(baseCode) ?? { debits: Money.zero(TAG), credits: Money.zero(TAG) };
  let widest = Money.zero(TAG);
  let everyCurrencyBalances = true;
  let anyDebits = false;
  for (const [, bucket] of buckets) {
    const gap = bucket.debits.minus(bucket.credits).abs();
    if (gap.greaterThan(widest)) widest = gap;
    if (!bucket.debits.differsByLessThan(bucket.credits, JOURNAL_BALANCE_TOLERANCE)) {
      everyCurrencyBalances = false;
    }
    if (bucket.debits.isPositive()) anyDebits = true;
  }

  return {
    totalDebit: base.debits.toFixed2(),
    totalCredit: base.credits.toFixed2(),
    difference: widest.toFixed2(),
    balanced: everyCurrencyBalances && anyDebits,
  };
}

/** A line is sendable when it names an account and holds exactly one side. */
export function isCompleteLine(line: EditorLine): boolean {
  const debit = parseAmount(line.debit);
  const credit = parseAmount(line.credit);
  const oneSide = (debit !== null && debit.isPositive()) !== (credit !== null && credit.isPositive());
  return line.accountId !== null && oneSide;
}

/** The wire shape of the complete lines (the blanks stay on screen only). */
export function toLineInputs(lines: readonly EditorLine[]): {
  accountId: string;
  debit?: string;
  credit?: string;
  memo?: string;
  currency?: string;
}[] {
  return lines.filter(isCompleteLine).map((line) => {
    const debit = parseAmount(line.debit);
    const credit = parseAmount(line.credit);
    return {
      accountId: line.accountId!,
      ...(debit !== null && debit.isPositive() ? { debit: debit.toFixed2() } : {}),
      ...(credit !== null && credit.isPositive() ? { credit: credit.toFixed2() } : {}),
      ...(line.memo.trim() !== '' ? { memo: line.memo.trim() } : {}),
      // Omitted means the base — the engine's own default (BR-FIN-02-01).
      ...(line.currency.trim() !== '' ? { currency: line.currency.trim() } : {}),
    };
  });
}
